import { isEuDomiciled } from '@crm/shared';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';

import type { Opportunity } from '../../lib/api';
import { formatChfCompact, formatBps } from '../../lib/format';

type Props = {
  opportunity: Opportunity;
};

function getAlerts(opportunity: Opportunity) {
  const kycExpired = opportunity.kyc_status === 'expired';
  const euReverse =
    isEuDomiciled(opportunity.domiciled_country) &&
    ['compliance_review', 'proposal', 'onboarding'].includes(opportunity.stage) &&
    !opportunity.reverse_solicitation_evidence;
  return { kycExpired, euReverse };
}

function CardBody({ opportunity }: { opportunity: Opportunity }) {
  const { kycExpired, euReverse } = getAlerts(opportunity);
  const showDot = kycExpired || euReverse;
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-base leading-snug text-graphite truncate flex-1">
          {opportunity.name}
        </h3>
        {showDot && (
          <span
            className="shrink-0 inline-block w-1.5 h-1.5 mt-2 rounded-full bg-signal"
            aria-label={kycExpired ? 'KYC expired' : 'Reverse solicitation to document'}
          />
        )}
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-x-3 text-xs font-tabular text-ink-secondary">
        <div>
          <dt className="sr-only">Expected amount</dt>
          <dd className="text-ink font-medium">
            {formatChfCompact(opportunity.initial_expected_amount)}
          </dd>
        </div>
        <div className="text-center">
          <dt className="sr-only">RoA</dt>
          <dd>{formatBps(opportunity.roa_bps)}</dd>
        </div>
        <div className="text-right">
          <dt className="sr-only">Target bank</dt>
          <dd className="truncate">{opportunity.target_bank?.code ?? '—'}</dd>
        </div>
      </dl>
    </>
  );
}

/**
 * Pure visual card — used by the DragOverlay ghost.
 */
export function OpportunityCardPresentation({
  opportunity,
  dragging = false,
}: {
  opportunity: Opportunity;
  dragging?: boolean;
}) {
  return (
    <div
      className={
        'card bg-surface px-4 py-3 block touch-manipulation select-none ' +
        (dragging ? 'shadow-[0_6px_24px_rgba(26,40,50,0.18)] ring-2 ring-graphite/10' : '')
      }
    >
      <CardBody opportunity={opportunity} />
    </div>
  );
}

/**
 * Draggable + clickable card.
 *
 * Drag is the default gesture (whole card). To open the detail, click on
 * the name (the inner Link stops propagation), or use the chevron Open
 * button in the footer.
 */
export function OpportunityCard({ opportunity }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: opportunity.id,
    data: { opportunity },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const { kycExpired, euReverse } = getAlerts(opportunity);
  const showDot = kycExpired || euReverse;

  return (
    <article
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="card bg-surface px-4 py-3 cursor-grab active:cursor-grabbing
                 hover:shadow-[0_2px_6px_rgba(26,40,50,0.10)]
                 transition-shadow duration-200
                 min-h-touch select-none touch-manipulation
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-graphite focus-visible:ring-offset-2"
      aria-roledescription="sortable opportunity card"
      aria-label={`${opportunity.name}, stage ${opportunity.stage}, ${formatChfCompact(opportunity.initial_expected_amount)}`}
      data-testid="opportunity-card"
    >
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/opportunities/${opportunity.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="font-display text-base leading-snug text-graphite truncate flex-1 hover:underline"
        >
          {opportunity.name}
        </Link>
        {showDot && (
          <span
            className="shrink-0 inline-block w-1.5 h-1.5 mt-2 rounded-full bg-signal"
            aria-label={kycExpired ? 'KYC expired' : 'Reverse solicitation to document'}
          />
        )}
      </div>
      <dl className="mt-2 grid grid-cols-3 gap-x-3 text-xs font-tabular text-ink-secondary">
        <div>
          <dt className="sr-only">Expected amount</dt>
          <dd className="text-ink font-medium">
            {formatChfCompact(opportunity.initial_expected_amount)}
          </dd>
        </div>
        <div className="text-center">
          <dt className="sr-only">RoA</dt>
          <dd>{formatBps(opportunity.roa_bps)}</dd>
        </div>
        <div className="text-right">
          <dt className="sr-only">Target bank</dt>
          <dd className="truncate">{opportunity.target_bank?.code ?? '—'}</dd>
        </div>
      </dl>
    </article>
  );
}
